import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DirectionsBoat as BoatIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { findDuplicateCustomers, DuplicateCustomerGroup, Customer, mergeCustomers, updateCustomer } from '../../../firebase/services/customerVesselService';
import { getAllSlips, Slip } from '../../../firebase/services/slipService';

interface CustomerFormData {
  firstName: string;
  lastName: string;
  companyName?: string;
  email: string;
  phone: string;
}

const DuplicateCustomersPage: React.FC = () => {
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateCustomerGroup[]>([]);
  const [slips, setSlips] = useState<Slip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateCustomerGroup | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [primaryCustomer, setPrimaryCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [groups, slipsData] = await Promise.all([
        findDuplicateCustomers(),
        getAllSlips()
      ]);
      setDuplicateGroups(groups);
      setSlips(slipsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsClick = (group: DuplicateCustomerGroup) => {
    setSelectedGroup(group);
    setDetailsDialogOpen(true);
  };

  const handleEditClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData({
      firstName: customer.firstName,
      lastName: customer.lastName,
      companyName: customer.companyName || '',
      email: customer.email || '',
      phone: customer.phone || ''
    });
    setEditDialogOpen(true);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      await updateCustomer(selectedCustomer.id, {
        ...selectedCustomer,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        email: formData.email,
        phone: formData.phone
      });
      
      setEditDialogOpen(false);
      await loadData();
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Failed to update customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMergeClick = (group: DuplicateCustomerGroup) => {
    setSelectedGroup(group);
    setPrimaryCustomer(group.customers[0]);
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!selectedGroup || !primaryCustomer) return;

    try {
      setLoading(true);
      setError(null);

      const customerIdsToMerge = selectedGroup.customers
        .filter(c => c.id !== primaryCustomer.id)
        .map(c => c.id);

      await mergeCustomers(primaryCustomer.id, customerIdsToMerge);

      setMergeDialogOpen(false);
      setDetailsDialogOpen(false);
      await loadData();
    } catch (err) {
      console.error('Error merging customers:', err);
      setError('Failed to merge customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerSlips = (customerId: string) => {
    return slips.filter(slip => slip.customerId === customerId);
  };

  const CustomerDetailCard: React.FC<{ customer: Customer; isPrimary?: boolean }> = ({ customer, isPrimary }) => {
    const customerSlips = getCustomerSlips(customer.id);
    
    return (
      <Card sx={{ mb: 2, border: isPrimary ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
        <CardContent>
          {isPrimary && (
            <Chip 
              label="Primary Customer" 
              color="primary" 
              size="small" 
              sx={{ mb: 1 }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">
              {customer.companyName || `${customer.firstName} ${customer.lastName}`}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{customer.firstName} {customer.lastName}</Typography>
            </Grid>
            {customer.companyName && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Company</Typography>
                <Typography variant="body1">{customer.companyName}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{customer.email || 'N/A'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{customer.phone || 'N/A'}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {customer.vessels && customer.vessels.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Vessels ({customer.vessels.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {customer.vessels.map((vessel) => (
                  <Chip
                    key={vessel.vesselId}
                    icon={<BoatIcon />}
                    label={`${vessel.name || vessel.registrationNumber} (${vessel.year} ${vessel.make})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {customerSlips.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Assigned Slips ({customerSlips.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {customerSlips.map((slip) => (
                  <Chip
                    key={slip.id}
                    label={`Slip ${slip.slipNumber}`}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
        <CardActions>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditClick(customer)}
          >
            Edit
          </Button>
        </CardActions>
      </Card>
    );
  };

  if (loading && duplicateGroups.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Duplicate Customers
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/business/customers')}
        >
          Back to Customers
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {duplicateGroups.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No duplicate customers found.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {duplicateGroups.map((group, index) => (
            <Accordion key={`${group.matchType}-${group.matchValue}-${index}`} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Chip
                    label={group.matchType.charAt(0).toUpperCase() + group.matchType.slice(1)}
                    color={
                      group.matchType === 'email' ? 'primary' :
                      group.matchType === 'phone' ? 'secondary' :
                      group.matchType === 'company' ? 'info' : 'default'
                    }
                    size="small"
                  />
                  <Typography variant="h6">{group.matchValue}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({group.customers.length} customers)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {group.customers.map((customer) => (
                    <Grid item xs={12} md={6} key={customer.id}>
                      <CustomerDetailCard customer={customer} />
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleMergeClick(group)}
                  >
                    Merge Customers
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleDetailsClick(group)}
                  >
                    View Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Duplicate Customer Details - {selectedGroup?.matchType}: {selectedGroup?.matchValue}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {selectedGroup?.customers.map((customer, index) => (
              <Grid item xs={12} md={6} key={customer.id}>
                <CustomerDetailCard customer={customer} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setDetailsDialogOpen(false);
              if (selectedGroup) handleMergeClick(selectedGroup);
            }}
          >
            Merge These Customers
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merge Duplicate Customers</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select the primary customer record to keep. All vessels, slips, and other data from the other records will be merged into this one.
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Primary Customer</InputLabel>
            <Select
              value={primaryCustomer?.id || ''}
              onChange={(e) => {
                const customer = selectedGroup?.customers.find(c => c.id === e.target.value);
                if (customer) setPrimaryCustomer(customer);
              }}
              label="Primary Customer"
            >
              {selectedGroup?.customers.map(customer => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                  {customer.email && ` (${customer.email})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {primaryCustomer && selectedGroup && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Preview of Primary Customer:</Typography>
              <CustomerDetailCard customer={primaryCustomer} isPrimary />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The following customers will be merged into the primary customer:
              </Typography>
              {selectedGroup.customers
                .filter(c => c.id !== primaryCustomer.id)
                .map(customer => (
                  <Box key={customer.id} sx={{ ml: 2, mt: 1 }}>
                    <Typography variant="body2">
                      • {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                      {customer.vessels?.length ? ` (${customer.vessels.length} vessels)` : ''}
                      {getCustomerSlips(customer.id).length ? ` (${getCustomerSlips(customer.id).length} slips)` : ''}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            color="primary"
            disabled={!primaryCustomer}
          >
            Merge Customers
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateCustomersPage; 